import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

const quickLinks = [
  { name: 'Beginner Guide', url: '/article/cssbuy-spreadsheet-guide', internal: true },
  { name: 'Shopping Guide', url: '/article/shopping-guide', internal: true },
  { name: 'QC Master Guide', url: '/article/how-to-qc', internal: true },
  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html', internal: false },
  { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html', internal: false },
  { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html', internal: false },
  { name: 'Other Stuff', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html', internal: false },
];

const socialLinks = [
  { name: 'Instagram', url: '#', icon: 'ri-instagram-line' },
  { name: 'Reddit', url: '#', icon: 'ri-reddit-line' },
  { name: 'Twitter', url: '#', icon: 'ri-twitter-x-line' },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const hp = formData.get('website_alt') as string;
    if (hp && hp.trim() !== '') {
      setStatus('success');
      setEmail('');
      form.reset();
      return;
    }
    setStatus('idle');
    try {
      const res = await fetch('https://readdy.ai/api/form/d96aoakut53jbls7kll0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const responseText = await res.text();
      if (res.ok) {
        try {
          const parsed = JSON.parse(responseText);
          if (parsed?.code === 'OK') {
            setStatus('success');
            setEmail('');
            form.reset();
          } else {
            setStatus('error');
            setErrorMsg(parsed?.meta?.message || 'Something went wrong. Please try again.');
          }
        } catch {
          setStatus('success');
          setEmail('');
          form.reset();
        }
      } else {
        setStatus('error');
        try {
          const parsed = JSON.parse(responseText);
          setErrorMsg(parsed?.meta?.message || 'Something went wrong. Please try again.');
        } catch {
          setErrorMsg('Something went wrong. Please try again.');
        }
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please check your connection and try again.');
    }
  }, []);

  return (
    <footer className="relative mx-4 md:mx-6 mb-4 md:mb-6 rounded-3xl overflow-hidden" style={{ backgroundColor: 'oklch(0.15 0.01 270)' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 mb-16">
          <div className="md:col-span-5">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
              Stay Updated
            </h3>
            <p className="text-sm text-foreground-300 mb-6">
              Get the latest CSSBuy spreadsheet updates, new category links, and shopping tips straight to your inbox.
            </p>

            <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="newsletter-hp"
              />
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
                  placeholder="Your email address"
                  className="w-full bg-transparent border-b border-foreground-500/40 pb-3 text-sm text-background-50 placeholder:text-foreground-500 outline-none focus:border-background-200 transition-colors duration-200"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-50 text-foreground-950 rounded-full text-sm font-semibold hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Subscribe
                <i className="ri-arrow-right-line"></i>
              </button>
              {status === 'success' && (
                <p className="text-xs text-accent-400">Thanks for subscribing! We will keep you updated.</p>
              )}
              {status === 'error' && (
                <p className="text-xs text-red-400">{errorMsg}</p>
              )}
            </form>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-sm font-semibold text-background-200 uppercase tracking-wider mb-5">
              Quick Links
            </h4>
            <nav className="flex flex-col gap-3">
              {quickLinks.map((link) => (
                link.internal ? (
                  <Link
                    key={link.name}
                    to={link.url}
                    className="text-sm text-foreground-400 hover:text-background-100 transition-colors duration-200 border-b border-transparent hover:border-foreground-400 pb-0.5 cursor-pointer"
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-400 hover:text-background-100 transition-colors duration-200 border-b border-transparent hover:border-foreground-400 pb-0.5 cursor-pointer"
                  >
                    {link.name}
                  </a>
                )
              ))}
            </nav>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-sm font-semibold text-background-200 uppercase tracking-wider mb-5">
              Support
            </h4>
            <p className="text-sm text-foreground-400 mb-1">
              <a href="mailto:hello@cssspreadsheet.casa" className="hover:text-background-100 transition-colors duration-200">hello@cssspreadsheet.casa</a>
            </p>
            <div className="mt-8">
              <h4 className="text-sm font-semibold text-background-200 uppercase tracking-wider mb-3">
                Address
              </h4>
              <p className="text-sm text-foreground-400 leading-relaxed">
                CSSSpreadsheet Resource Center<br />
                Online Community Worldwide
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-foreground-500/20 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener nofollow"
                className="text-foreground-400 hover:text-background-100 transition-colors duration-200 border-b border-transparent hover:border-foreground-400 pb-0.5 text-sm cursor-pointer"
                aria-label={social.name}
              >
                <i className={`${social.icon} text-lg`}></i>
              </a>
            ))}
          </div>

          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} CSSSpreadsheet.casa. All rights reserved.
          </p>

          <div className="flex items-center gap-5">
            <a href="#" className="text-xs text-foreground-500 hover:text-background-100 transition-colors duration-200 border-b border-transparent hover:border-foreground-400 pb-0.5">
              Privacy
            </a>
            <a href="#" className="text-xs text-foreground-500 hover:text-background-100 transition-colors duration-200 border-b border-transparent hover:border-foreground-400 pb-0.5">
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}